// The derived, disposable dataset cache, distinct from favorites in
// persistence.js. Everything here is defensive because IndexedDB may be
// unavailable (private mode, SSR-ish test envs), full (quota), or hold
// stale/garbage data: reads degrade to null (go to the network), writes never
// throw. The kv backend is injected as a seam so tests can drive a Map-backed
// fake without a real IndexedDB.

import { get as idbGet, set as idbSet } from "idb-keyval";

const defaultKv = { get: idbGet, set: idbSet };

// Bump the suffix if the cached shape ever changes.
export const SCHEDULE_CACHE_KEY = "devconf.schedule.v1";

// Never throws: an unavailable or full IndexedDB just means this device won't work
// offline yet.
export async function saveSchedule(payload, kv = defaultKv) {
  try {
    await kv.set(SCHEDULE_CACHE_KEY, payload);
  } catch {
    /* ignore: IndexedDB may be full or unavailable */
  }
}

// Read the cached schedule payload, or `null` if there's nothing usable (caller falls
// back to the network). Never throws.
export async function readSchedule(kv = defaultKv) {
  try {
    const value = await kv.get(SCHEDULE_CACHE_KEY);
    if (!isLoadPayload(value)) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

// Reconstruct the detail view's offline fallback for `id` from the cached dataset, or
// `null` if there's no usable cache or the id isn't present. Online the view fetches
// `/api/sessions/:id` (which embeds `track` + `room`) plus `/api/speakers`; this returns
// the SAME data derived from the one cached payload, so the two must stay in sync if
// that online shape changes. Never throws.
export async function readSessionDetail(id, kv = defaultKv) {
  const data = await readSchedule(kv);
  if (!data) {
    return null;
  }
  const session = data.sessions.find((s) => s.id === id);
  if (!session) {
    return null;
  }
  // Mirror the mock's `/sessions/:id` route: a missing track/room is left
  // `undefined` (the view renders its "Session"/"TBA" fallback), the same UX as
  // when the API returns `null`.
  const track = data.tracks.find((t) => t.id === session.trackId);
  const room = data.rooms.find((r) => r.id === session.roomId);
  return { session: { ...session, track, room }, speakers: data.speakers };
}

// Cheap insurance against a stale or hand-mangled cache entry from a previous version.
function isLoadPayload(value) {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const v =  (value);
  return Array.isArray(v.sessions) && Array.isArray(v.tracks) && Array.isArray(v.rooms) && Array.isArray(v.speakers);
}
